import { useState, useEffect } from "react";
import * as Icons from "lucide-react";
import { cn } from "@/lib/utils";

// Lead Workspace 3.0 — collapsible, grouped in-page navigation.
// Groups reflect the staff mental model: daily WORKSPACE, client-service
// ENGAGEMENT, and as-needed SUPPORTING reference. Allison is intentionally NOT
// a nav item (embedded in the AI Command Card + briefing drawer).
export const NAV_GROUPS = [
  {
    label: "Workspace",
    items: [
      ["overview",     "Overview",       "LayoutDashboard"],
      ["tax",          "Tax Profile",    "FileUser"],
      ["communication","Communications", "MessageSquare"],
      ["documents",    "Documents",      "FileText"],
      ["tasks",        "Tasks",          "CheckSquare"],
      ["meetings",     "Meetings",       "Calendar"],
    ],
  },
  {
    label: "Engagement",
    items: [
      ["estimates",    "Estimates",     "Calculator"],
      ["engagements",  "Engagements",   "Briefcase"],
      ["invoices",     "Invoices",      "Receipt"],
      ["services",     "Services",      "Package"],
      ["organizers",   "Organizers",    "ClipboardList"],
      ["production",   "Production",    "Boxes"],
    ],
  },
  {
    label: "Supporting",
    items: [
      ["workflow",   "Workflow",        "GitMerge"],
      ["notes",      "Notes",           "StickyNote"],
      ["timeline",   "Timeline",        "Activity"],
      ["provenance", "Comm Provenance", "ShieldCheck"],
      ["audit",      "Audit History",   "ShieldCheck"],
    ],
  },
];

const ALL_ITEMS = NAV_GROUPS.flatMap((g) => g.items);

export default function WorkspaceNav({ tab, setTab, groups = NAV_GROUPS, idPrefix = "ws", navTestId = "workspace-nav", selectTestId = "workspace-nav-select", storageKey = "tf.ws.navCollapsed" }) {
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem(storageKey) === "1"; } catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem(storageKey, collapsed ? "1" : "0"); } catch { /* ignore */ }
  }, [collapsed, storageKey]);

  return (
    <>
      {/* Mobile / tablet: grouped dropdown navigator */}
      <div className="lg:hidden mb-4">
        <label className="sr-only" htmlFor={`${idPrefix}-nav-select`}>Workspace section</label>
        <select id={`${idPrefix}-nav-select`} data-testid={selectTestId} value={tab}
          onChange={(e) => setTab(e.target.value)}
          className="w-full bg-white border border-slate-300 rounded-lg px-3 py-2.5 text-sm font-medium text-slate-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold">
          {groups.map((g) => (
            <optgroup key={g.label} label={g.label}>
              {g.items.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </optgroup>
          ))}
        </select>
      </div>

      {/* Desktop: collapsible vertical grouped rail */}
      <nav aria-label="Workspace navigation" data-testid={navTestId}
        className={cn("hidden lg:flex flex-col shrink-0 self-start sticky top-[92px] bg-white border border-slate-200 rounded-xl shadow-sm py-3 transition-[width] duration-200",
          collapsed ? "w-16 px-2" : "w-56 px-3")}>
        <button type="button" onClick={() => setCollapsed((v) => !v)} data-testid={`${idPrefix}-nav-toggle`}
          aria-expanded={!collapsed} aria-label={collapsed ? "Expand navigation" : "Collapse navigation"}
          className={cn("flex items-center rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-50 h-9 mb-2 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold",
            collapsed ? "justify-center w-full" : "justify-end px-2")}>
          <Icons.PanelLeftClose className={cn("h-4 w-4 transition-transform", collapsed && "rotate-180")} />
        </button>

        <div className="space-y-4">
          {groups.map((g) => (
            <div key={g.label}>
              {!collapsed && (
                <div className="px-2 mb-1 text-[10px] font-bold uppercase tracking-wider text-slate-400">{g.label}</div>
              )}
              {collapsed && <div className="mx-2 mb-1.5 border-t border-slate-100" />}
              <div className="space-y-0.5">
                {g.items.map(([v, l, icon]) => {
                  const Icon = Icons[icon] || Icons.Circle;
                  const active = tab === v;
                  return (
                    <button key={v} type="button" onClick={() => setTab(v)} data-testid={`${idPrefix}-nav-${v}`}
                      data-state={active ? "active" : "inactive"} aria-current={active ? "page" : undefined}
                      title={collapsed ? l : undefined}
                      className={cn("group flex items-center gap-2.5 w-full rounded-lg text-sm transition-colors h-9 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold",
                        collapsed ? "justify-center px-0" : "px-2.5",
                        active
                          ? "bg-slate-100 text-charcoal font-semibold border-l-2 border-charcoal"
                          : "text-slate-500 hover:bg-slate-50 hover:text-slate-900 border-l-2 border-transparent")}>
                      <Icon className={cn("h-[18px] w-[18px] shrink-0", active ? "text-charcoal" : "text-slate-400 group-hover:text-slate-600")} />
                      {!collapsed && <span className="truncate">{l}</span>}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </nav>
    </>
  );
}

export { ALL_ITEMS };
